import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { LucideArrowDown } from '@lucide/angular';
import { PaginationEvent } from '../pagination/pagination-footer.component';
import { PtlTableCellDirective } from './ptl-table-cell.directive';

export interface PtlTableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
  sortable?: boolean;
}

export interface PtlTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Shared v3 data table. Owns the card chrome, header (with optional client-side
 * sort), body, and a pinned pagination footer. The consumer projects a
 * `[table-toolbar]` element (search / filters / actions) into the top bar, and
 * `<ng-template ptlCell="key" let-row>` templates for custom cell content.
 * Search / filtering stay in the parent: it feeds already-filtered `data`.
 */
@Component({
  selector: 'ptl-data-table',
  templateUrl: './ptl-table.component.html',
  styleUrls: ['./ptl-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PtlTableComponent implements AfterContentInit, OnChanges {
  @Input() columns: PtlTableColumn[] = [];
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Input() data: Record<string, any>[] = [];
  /** 0 = no pagination (footer hidden). Updated by the footer's "rows per page" select. */
  @Input() pageSize = 0;
  /** Footer label; "{count}" is replaced with the total row count. */
  @Input() totalLabel = '';
  /** Row property used for trackBy (falls back to index). */
  @Input() trackKey = '';
  /** Renders the header row with a dark background and light text. */
  @Input() darkHeader = false;

  @Output() sortChange = new EventEmitter<PtlTableSort>();
  /** Emits the clicked body row. Opt-in: rows are only interactive when a consumer binds this. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Output() rowClick = new EventEmitter<Record<string, any>>();

  @ContentChildren(PtlTableCellDirective) private cellDirs!: QueryList<PtlTableCellDirective>;

  protected readonly LucideArrowDown = LucideArrowDown;

  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';
  page = 0;

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private cellMap = new Map<string, TemplateRef<any>>();

  ngOnChanges(): void {
    if (this.pageSize && this.page * this.pageSize >= this.data.length) {
      this.page = 0;
    }
  }

  ngAfterContentInit(): void {
    this.buildCellMap();
    this.cellDirs.changes.subscribe(() => this.buildCellMap());
  }

  private buildCellMap(): void {
    this.cellMap.clear();
    this.cellDirs.forEach((d) => this.cellMap.set(d.column, d.template));
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  cellTemplate(key: string): TemplateRef<any> | null {
    return this.cellMap.get(key) ?? null;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  get sortedRows(): Record<string, any>[] {
    const key = this.sortKey;
    if (!key) {
      return this.data;
    }
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.data].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  get pagedRows(): Record<string, any>[] {
    const rows = this.sortedRows;
    if (!this.pageSize) {
      return rows;
    }
    const start = this.page * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  get total(): number {
    return this.data.length;
  }

  /** Rows become interactive (cursor, keyboard, click) only when a consumer subscribes to (rowClick). */
  get rowClickable(): boolean {
    return this.rowClick.observed;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onRowClick(row: Record<string, any>): void {
    if (this.rowClickable) {
      this.rowClick.emit(row);
    }
  }

  get totalText(): string {
    return this.totalLabel.replace('{count}', String(this.total));
  }

  onSort(col: PtlTableColumn): void {
    if (!col.sortable) {
      return;
    }
    if (this.sortKey === col.key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = col.key;
      this.sortDir = 'asc';
    }
    this.page = 0;
    this.sortChange.emit({ key: this.sortKey, direction: this.sortDir });
  }

  onPage(event: PaginationEvent): void {
    this.page = Math.max(0, event.currentPage - 1);
  }

  onPageSize(size: number): void {
    this.pageSize = size;
    this.page = 0;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  trackRow = (index: number, row: Record<string, any>): unknown => (this.trackKey ? (row[this.trackKey] ?? index) : index);
}
